<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ title }}
      </p>
    </header>
    <section class="modal-card-body">
      <b-table
        :data="items"
        :loading="isLoading"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
      >
        <b-table-column
          field="id"
          :label="$t('attributes.id')"
          :visible="false"
          v-slot="props"
        >
          {{ props.row.id }}
        </b-table-column>

        <b-table-column
          field="area"
          :label="$t('attributes.contact_area')"
          :width="150"
          v-slot="props"
        >
          <p v-if="!props.row.editing && props.row.id">
            {{ props.row.area }}
          </p>
          <b-field
            v-else
            :type="!!props.row.modify.errors['area'] ? 'is-danger' : ''"
          >
            <b-input
              v-model="props.row.modify.area"
              :placeholder="$t('attributes.contact_area')"
              @input="props.row.modify.errors['area'] = null"
            ></b-input>
          </b-field>
        </b-table-column>

        <b-table-column
          field="name"
          :label="$t('attributes.name')"
          :width="120"
          v-slot="props"
        >
          <p v-if="!props.row.editing && props.row.id">
            {{ props.row.name }}
          </p>
          <b-field
            v-else
            :type="!!props.row.modify.errors['name'] ? 'is-danger' : ''"
          >
            <b-input
              v-model="props.row.modify.name"
              :placeholder="$t('attributes.name')"
              @input="props.row.modify.errors['name'] = null"
            ></b-input>
          </b-field>
        </b-table-column>

        <b-table-column
          field="tel"
          :label="$t('attributes.tel')"
          :width="250"
          v-slot="props"
        >
          <p v-if="!props.row.editing && props.row.id">
            {{ props.row.tel }}
          </p>
          <b-field
            v-else
            :type="!!props.row.modify.errors['tel'] ? 'is-danger' : ''"
          >
            <b-input
              v-model="props.row.modify.tel"
              :placeholder="$t('attributes.tel')"
              @input="props.row.modify.errors['tel'] = null"
            ></b-input>
          </b-field>
        </b-table-column>

        <b-table-column
          field="email"
          :label="$t('attributes.email')"
          v-slot="props"
        >
          <p v-if="!props.row.editing && props.row.id">
            {{ props.row.email }}
          </p>
          <b-field
            v-else
            :type="!!props.row.modify.errors['email'] ? 'is-danger' : ''"
          >
            <b-input
              v-model="props.row.modify.email"
              :placeholder="$t('attributes.email')"
              @input="props.row.modify.errors['email'] = null"
            ></b-input>
          </b-field>
        </b-table-column>

        <b-table-column :width="160" v-slot="props">
          <div v-if="!props.row.id" class="buttons">
            <b-button
              type="is-warning"
              class="is-fullwidth"
              @click.prevent="onAdd(props.row)"
            >
              {{ $t('attributes.add') }}
            </b-button>
          </div>
          <div v-else-if="!props.row.editing" class="buttons">
            <b-button
              type="is-primary"
              @click.prevent="editing(props.row)"
              style="margin-right: 16px;"
            >
              {{ $t('attributes.modify') }}
            </b-button>
            <b-button type="is-danger" @click.prevent="onDelete(props.row)">
              {{ $t('attributes.delete') }}
            </b-button>
          </div>
          <div v-else class="buttons">
            <b-button
              type="is-primary"
              @click.prevent="doneEdit(props.row)"
              style="margin-right: 16px;"
            >
              {{ $t('attributes.update') }}
            </b-button>
            <b-button
              type="is-success"
              outlined
              @click.prevent="cancelEdit(props.row)"
            >
              {{ $t('attributes.cancel') }}
            </b-button>
          </div>
        </b-table-column>
      </b-table>
    </section>
    <footer class="modal-card-foot">
      <b-button @click="$parent.close()">
        {{ $t('attributes.close') }}
      </b-button>
    </footer>
  </div>
</template>

<script>
import { FIRM_COLLABORATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'ContactsForm',

  props: {
    id: {
      required: true,
      type: Number,
      default: null
    },
    name: {
      required: true,
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isLoading: false,
      items: []
    }
  },
  computed: {
    title() {
      return (
        this.$t('attributes.modify') +
        this.$t('attributes.contacts') +
        ' - ' +
        this.name
      )
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const api = `${FIRM_COLLABORATES_API}/${this.id}`

      this.isLoading = true
      try {
        const res = await this.axios.get(api)
        const data = res.data.data

        this.items = data.contacts.map(item => ({
          ...item,
          editing: false,
          modify: {
            area: item.area,
            name: item.name,
            tel: item.tel,
            email: item.email,
            errors: {}
          }
        }))
        this.items.push({
          id: null,
          area: '',
          name: '',
          tel: '',
          email: '',
          modify: {
            area: '',
            name: '',
            tel: '',
            email: '',
            errors: {}
          }
        })
      } catch (e) {
        this.items = []

        notifyService.fetch_error('collaborate', e)
      } finally {
        this.isLoading = false
      }
    },
    isValid(item) {
      const attributes = {
        area: 20,
        name: 20,
        tel: 20,
        email: 50
      }
      let valid = true

      const errors = {}
      Object.keys(attributes).forEach(att => {
        if (!item.modify[att]) {
          valid = false
          errors[att] = '欄位不可空白'
        } else if (item.modify[att].length > attributes[att]) {
          valid = false
          errors[att] = '長度不可超過' + attributes[att]
        } else {
          errors[att] = null
        }
      })

      if (!item.modify.email.includes('@')) {
        valid = false
        errors['email'] = 'email格式不正確'
      }

      item.modify.errors = errors
      return valid
    },
    onAdd(item) {
      if (!this.isValid(item)) {
        return
      }

      const data = {
        area: item.modify.area,
        name: item.modify.name,
        tel: item.modify.tel,
        email: item.modify.email
      }
      this.addContact(data)
    },
    editing(item) {
      item.editing = true
    },
    doneEdit(item) {
      if (!this.isValid(item)) {
        return
      }

      const data = {
        area: item.modify.area,
        name: item.modify.name,
        tel: item.modify.tel,
        email: item.modify.email
      }
      this.updateContact(item.id, data)
    },
    cancelEdit(item) {
      item.editing = false
      item.modify = {
        area: item.area,
        name: item.name,
        tel: item.tel,
        email: item.email,
        errors: {}
      }
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          this.deleteContact(item.id)
        }
      })
    },
    async addContact(item) {
      const api = `${FIRM_COLLABORATES_API}/${this.id}/contacts`

      this.isLoading = true
      try {
        const res = await this.axios.post(api, item)
        const data = res.data.data

        this.items = this.items.filter(i => i.id)
        this.items.push({
          ...data,
          editing: false,
          modify: {
            area: data.area,
            name: data.name,
            tel: data.tel,
            email: data.email,
            errors: {}
          }
        })
        this.items.push({
          id: null,
          area: '',
          name: '',
          tel: '',
          email: '',
          modify: {
            area: '',
            name: '',
            tel: '',
            email: '',
            errors: {}
          }
        })
        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.isLoading = false
      }
    },
    async updateContact(id, item) {
      const api = `${FIRM_COLLABORATES_API}/contacts/${id}`

      this.isLoading = true
      try {
        const res = await this.axios.put(api, item)
        const data = res.data.data
        const idx = this.items.findIndex(i => i.id === id)
        this.items[idx].editing = false
        this.items[idx].area = data.area
        this.items[idx].name = data.name
        this.items[idx].tel = data.tel
        this.items[idx].email = data.email
        this.items[idx].modify = {
          area: data.area,
          name: data.name,
          tel: data.tel,
          email: data.email,
          errors: {}
        }
        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.isLoading = false
      }
    },
    async deleteContact(id) {
      const api = `${FIRM_COLLABORATES_API}/contacts/${id}`

      this.isLoading = true
      try {
        await this.axios.delete(api)
        this.items = this.items.filter(i => i.id !== id)

        notifyService.success(this.$t('attributes.delete_succeed'))
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  min-width: 960px;
  min-height: 540px;

  .table {
    td {
      vertical-align: middle;

      .control {
        margin: -0.5rem -0.75rem;
      }
    }
  }
}

@media (max-width: 960px) {
  .modal-card {
    min-width: 760px;
  }
}

@media (max-width: 768px) {
  .modal-card {
    min-width: auto;
  }
}
</style>
